<template>
    <aside
        class="h-screen fixed z-[100] lg:z-[1] top-0 left-0 transition-transform -translate-x-full lg:translate-x-0
        w-full grid lg:static lg:grid grid-rows-[auto_1fr]
        lg:grid-rows-[5rem_1fr] lg:w-72 text-white bg-dark-blue v-tour-no-indents"
        :class="{'translate-x-0': state.showMobileMenu}"
        data-v-step="dashboard:menu"
    >
        <div
            class="py-2 lg:py-3 px-4 lg:px-6 bg-white flex items-center justify-between pt-safe border-b border-b-medium-light-gray"
        >
            <div class="lg:h-full flex items-center ">
                <RouterLink
                    :to="{ name: 'dashboard' }"
                >
                    <img
                        src="../../assets/images/Westfalen_Medical_PW_M_red_P_RGB.svg"
                        class="h-12"
                        alt="Log0 Westfalen Medical"
                    >
                </RouterLink>
                <LoadingSpinner
                    v-if="state.loading"
                    class="scale-25 -translate-y-1"
                />
            </div>
            <button
                type="button"
                class="grid lg:hidden gap-1 cursor-pointer p-2 -my-2 -mr-2"
                @click="state.showMobileMenu = false"
            >
                <span class="block h-[3px] w-6 bg-wgm-gray-800 rotate-45 translate-y-[calc(100%+1px)]"></span>
                <span class="block h-[3px] w-6 bg-wgm-gray-800 -rotate-45 -translate-y-full"></span>
            </button>
        </div>
        <nav class="flex flex-col pt-4 lg:pt-8 gap-y-6 lg:gap-y-8 overflow-y-auto">
            <SidebarNavGroup>
                <SidebarLink
                    route-name="dashboard"
                    :label="$t('dashboard.sidebar.dashboard')"
                    :icon="IconDashboard"
                />
            </SidebarNavGroup>
            <SidebarNavGroup
                v-if="auth.hasPermissionTo(Permission.Module_TrackingSystem_Global_View) && auth.hasActiveModule(Modules.TrackingSystem)"
                :header="$t('dashboard.sidebar.tracking-system.title')"
            >
                <SidebarLink
                    v-if="auth.hasPermissionTo(Permission.Module_TrackingSystem_Orders_View)"
                    route-name="module.tracking-system.order"
                    :route-parameters="{ caregroupUuid: auth.user?.caregroups?.[0].uuid }"
                    :label="$t('dashboard.sidebar.tracking-system.orders')"
                    :icon="IconDelivery"
                />
                <SidebarLink
                    v-if="auth.hasPermissionTo(Permission.Module_TrackingSystem_Locations_ViewAll)"
                    route-name="module.tracking-system.locations"
                    :route-parameters="{ caregroupUuid: auth.user?.caregroups?.[0].uuid }"
                    :label="$t('dashboard.sidebar.tracking-system.locations')"
                    :icon="IconLocation"
                />
                <SidebarLink
                    v-if="auth.hasPermissionTo(Permission.Module_TrackingSystem_Stock_View)"
                    route-name="module.tracking-system.stock"
                    :route-parameters="{ caregroupUuid: auth.user?.caregroups?.[0].uuid }"
                    :label="$t('dashboard.sidebar.tracking-system.stock')"
                    :icon="IconBoxes"
                />
                <SidebarLink
                    v-if="auth.hasPermissionTo(Permission.Module_TrackingSystem_Barcodes_View)"
                    route-name="module.tracking-system.barcodes"
                    :route-parameters="{ caregroupUuid: auth.user?.caregroups?.[0].uuid }"
                    :label="$t('dashboard.sidebar.tracking-system.barcodes')"
                    :icon="IconBarcode"
                />
                <SidebarLink
                    v-if="auth.hasPermissionTo(Permission.Module_TrackingSystem_Invoices_View)"
                    route-name="module.tracking-system.invoice"
                    :route-parameters="{ caregroupUuid: auth.user?.caregroups?.[0].uuid }"
                    :label="$t('dashboard.sidebar.tracking-system.invoices')"
                    :icon="IconFiles"
                />
            </SidebarNavGroup>
            <SidebarNavGroup
                :header="$t('dashboard.sidebar.therapy')"
            >
                <SidebarLink
                    v-if="auth.hasPermissionTo(Permission.TherapyGlobal_Overview_View)"
                    route-name="therapy.overview"
                    :label="$t('dashboard.sidebar.therapy_overviews')"
                    :icon="IconEye"
                    data-v-step="sidebar:therapy-overviews"
                />
                <SidebarParentLink
                    v-if="therapies.size > 0"
                    :label="$t('dashboard.sidebar.manage_therapy_requests')"
                    :icon="IconUsers"
                    route-group="therapy"
                    data-v-step="sidebar:manage-therapy-requests"
                >
                    <template
                        v-for="therapy in [...therapies.keys()]"
                        :key="therapy"
                    >
                        <SidebarLink
                            :route-name="`therapy.${therapy}`"
                            :label="$t(`therapy.therapy_type.${therapy}`)"
                            :is-child="true"
                        />
                    </template>
                </SidebarParentLink>
            </SidebarNavGroup>
            <SidebarNavGroup
                v-if="isLocal"
                header="Development"
            >
                <SidebarParentLink
                    label="Stijl gids"
                    :icon="IconPaintBrush"
                    route-group="dev.styleguide"
                >
                    <SidebarLink
                        route-name="dev.styleguide.colours"
                        label="Kleuren"
                        :is-child="true"
                    />
                </SidebarParentLink>
                <SidebarLink
                    route-name="dev.translations"
                    label="Vertalingen"
                    :icon="IconTranslate"
                />
                <SidebarLink
                    v-if="isLocal"
                    route-name="dev.testing"
                    label="Triggers"
                    :icon="IconFlask"
                />
            </SidebarNavGroup>
            <SidebarNavGroup
                v-if="isTest"
                header="Testing"
            >
                <SidebarLink
                    route-name="dev.testing"
                    label="Triggers"
                    :icon="IconFlask"
                />
            </SidebarNavGroup>
            <SidebarNavGroup :header="$t('dashboard.sidebar.my_westfalen')">
                <SidebarParentLink
                    :label="$t('dashboard.sidebar.users')"
                    :icon="IconUsers"
                    route-group="users"
                >
                    <SidebarLink
                        route-name="users.overview"
                        :label="$t('dashboard.sidebar.users_overview')"
                        :is-child="true"
                    />
                    <SidebarLink
                        route-name="users.create"
                        :label="$t('dashboard.sidebar.users_create')"
                        :is-child="true"
                    />
                    <SidebarLink
                        route-name="users.validate"
                        :label="$t('dashboard.sidebar.users_validate')"
                        :is-child="true"
                    />
                </SidebarParentLink>
                <SidebarLink
                    route-name="patients.overview"
                    :label="$t('dashboard.sidebar.patients_overview')"
                    :icon="IconPatients"
                />
                <SidebarLink
                    v-if="!belongsToSingleCaregroup"
                    route-name="caregroups.overview"
                    :label="$t('dashboard.sidebar.caregroups_overview')"
                    :icon="IconHospital"
                />
                <SidebarLink
                    v-if="belongsToSingleCaregroup"
                    :on-click="() => $router.push({ name: 'caregroups.show', params: { uuid: auth.user?.caregroups?.[0].uuid }})"
                    :label="$t('dashboard.sidebar.caregroup_show')"
                    :icon="IconHospital"
                    data-v-step="sidebar:caregroup-show"
                />
                <SidebarParentLink
                    :label="$t('dashboard.sidebar.counter_reading')"
                    :icon="IconGauge"
                    route-group="counter-reading"
                >
                    <SidebarLink
                        route-name="counter-reading.overview"
                        :label="$t('dashboard.sidebar.overview')"
                        :is-child="true"
                    />
                    <SidebarLink
                        route-name="counter-reading.create"
                        :label="$t('dashboard.sidebar.forward')"
                        :is-child="true"
                    />
                </SidebarParentLink>
            </SidebarNavGroup>
            <SidebarNavGroup
                v-if="auth.hasPermissionTo(Permission.Global_StatusAnnouncements_View)"
                :header="$t('dashboard.sidebar.status')"
            >
                <SidebarLink
                    route-name="status.announcement.overview"
                    :label="$t('dashboard.sidebar.status_announcements')"
                    :icon="IconSpeechBubble4"
                />
            </SidebarNavGroup>
            <SidebarNavGroup :header="$t('dashboard.sidebar.my_account')">
                <SidebarLink
                    route-name="my-account"
                    :label="$t('dashboard.sidebar.account_management')"
                    :icon="IconAccountManagement"
                />
                <SidebarLink
                    :on-click="() => auth.logout()"
                    :label="$t('dashboard.sidebar.logout')"
                    :icon="IconLogout"
                />
            </SidebarNavGroup>
        </nav>
        <span class="text-xs pl-2 pb-2">{{ version || 'debug' }}</span>
    </aside>
</template>

<script setup lang="ts">
import useAuth from '~/stores/Auth';
import IconDashboard from '~/components/Icons/IconDashboard.vue';
import IconEye from '~/components/Icons/IconEye.vue';
import IconUsers from '~/components/Icons/IconUsers.vue';
import IconHospital from '~/components/Icons/IconHospital.vue';
import IconLocation from '~/components/Icons/IconLocation.vue';
import IconLogout from '~/components/Icons/IconLogout.vue';
import IconGauge from '~/components/Icons/IconGauge.vue';
import IconAccountManagement from '~/components/Icons/IconAccountManagement.vue';
import IconPaintBrush from '~/components/Icons/IconPaintBrush.vue';
import IconPatients from '~/components/Icons/IconPatients.vue';
import IconTranslate from '~/components/Icons/IconTranslate.vue';
import IconFlask from '~/components/Icons/IconFlask.vue';
import useState from '~/stores/State';
import { Modules, Permission } from '~/typings/enums';
import IconDelivery from '~/components/Icons/IconDelivery.vue';
import IconFiles from '~/components/Icons/IconFiles.vue';
import IconBoxes from '~/components/Icons/IconBoxes.vue';
import IconBarcode from '~/components/Icons/IconBarcode.vue';
import useTherapy from '~/models/Therapy';
import IconSpeechBubble4 from '~/components/Icons/IconSpeechBubble4.vue';

const version = __APP_VERSION__;
const state = useState();
const auth = useAuth();
const { therapies } = useTherapy();
const isTest = import.meta.env.VITE_SERVER_ENVIRONMENT === 'test';
const isLocal = import.meta.env.VITE_SERVER_ENVIRONMENT === 'local';

const belongsToSingleCaregroup = computed(() => {
    if (
        !auth.user
        || !auth.hasPermissionTo(Permission.Global_CaregroupManagement_View)
        || auth.user.caregroups.length === 0
    ) {
        return false;
    }

    return auth.user.caregroups.length === 1;
});
</script>
